import mapKeys from 'lodash/mapKeys';

import { PRINTER_BRANDS, PRINTER_KINDS } from '~/main/printer/types';

export const SUPPORTED_VENDORS = {
  [PRINTER_BRANDS.EPSON]: ['04b8', '0570', '03f8'],
  [PRINTER_BRANDS.STAR]: ['0519'],
  [PRINTER_BRANDS.LOGIC_CONTROLS]: ['0fe6'],
  [PRINTER_BRANDS.ZEBRA]: ['05e0', '0a5f'],
};

export type SupportedPrinterModel =
  | 'TM-T88IV'
  | 'TM-T88V'
  | 'TM-T88VI'
  | 'TM-T20'
  | 'TM-T20II'
  | 'TM-T20III'
  | 'TM-T82'
  | 'TM-M10'
  | 'TM-M30'
  | 'TM-M30II'
  | 'LR2000'
  | 'TSP650'
  | 'TSP100'
  | 'MC-PRINT3'
  | 'ZD410'
  | 'ZD410_300'
  | 'ZD411'
  | 'ZD420'
  | 'ZD421'
  | 'LP2824';

// Epson printers
export const TM_T88 = 'TM-T88' as const; // Every TM-T88*
export const TM_T88III = 'TM-T88III' as const;
const TM_T88IV = 'TM-T88IV' as const;
const TM_T88V = 'TM-T88V' as const;
const TM_T88VI = 'TM-T88VI' as const;
const TM_T20 = 'TM-T20' as const;
const TM_T20II = 'TM-T20II' as const;
const TM_T20III = 'TM-T20III' as const;
const TM_T82 = 'TM-T82' as const;
export const TM_M10 = 'TM-M10' as const;
export const TM_M30 = 'TM-M30' as const;
const TM_M30II = 'TM-M30II' as const;
export const LR2000 = 'LR2000' as const;

// Star printers
export const TSP100 = 'TSP100' as const;
export const TSP100_ECO = 'TSP100-ECO' as const;
const TSP650 = 'TSP650' as const;
const MC_PRINT2 = 'MC-PRINT2' as const;
export const MC_PRINT3 = 'MC-PRINT3' as const;

// Zebra printers
export const ZD410 = 'ZD410' as const;
const ZD410_300 = 'ZD410_300' as const;
export const ZD411 = 'ZD411' as const;
export const ZD420 = 'ZD420' as const;
export const ZD421 = 'ZD421' as const;
const LP2824 = 'LP2824' as const;

export const SUPPORTED_PRINTERS = {
  [PRINTER_BRANDS.EPSON]: {
    TM_T88III,
    TM_T88IV,
    TM_T88V,
    TM_T88VI,
    TM_T20,
    TM_T20II,
    TM_T82,
    TM_M10,
    TM_M30,
    TM_M30II,
  },
  [PRINTER_BRANDS.LOGIC_CONTROLS]: {
    LR2000,
  },
  [PRINTER_BRANDS.STAR]: {
    TSP650,
    TSP100,
    TSP100_ECO,
    MC_PRINT2,
    MC_PRINT3,
  },
  [PRINTER_BRANDS.ZEBRA]: {
    ZD410,
    ZD420,
    ZD421,
    LP2824,
  },
};

interface PrinterInformation {
  name: SupportedPrinterModel;
  kind: PRINTER_KINDS;
  brand: PRINTER_BRANDS;
  pid: number;
  vid?: number;
}

export const PRINTER_INFORMATION: Record<SupportedPrinterModel, PrinterInformation> = {
  [ZD410]: {
    name: ZD410,
    kind: PRINTER_KINDS.LABEL,
    brand: PRINTER_BRANDS.ZEBRA,
    pid: 284,
    vid: 0x0a5f,
  },
  [ZD410_300]: {
    name: ZD410,
    kind: PRINTER_KINDS.LABEL,
    brand: PRINTER_BRANDS.ZEBRA,
    pid: 286,
    vid: 0x0a5f,
  },
  [ZD411]: {
    name: ZD411,
    kind: PRINTER_KINDS.LABEL,
    brand: PRINTER_BRANDS.ZEBRA,
    pid: 385,
    vid: 0x0a5f,
  },
  [ZD420]: {
    name: ZD420,
    kind: PRINTER_KINDS.LABEL,
    brand: PRINTER_BRANDS.ZEBRA,
    pid: 288,
    vid: 0x0a5f,
  },
  [ZD421]: {
    name: ZD421,
    kind: PRINTER_KINDS.LABEL,
    brand: PRINTER_BRANDS.ZEBRA,
    pid: 389,
    vid: 0x0a5f,
  },
  [TM_T88IV]: {
    name: TM_T88IV,
    kind: PRINTER_KINDS.RECEIPT,
    brand: PRINTER_BRANDS.EPSON,
    pid: 514,
  },
  [TM_T88V]: { name: TM_T88V, kind: PRINTER_KINDS.RECEIPT, brand: PRINTER_BRANDS.EPSON, pid: 3586 },
  [TM_T88VI]: {
    name: TM_T88VI,
    kind: PRINTER_KINDS.RECEIPT,
    brand: PRINTER_BRANDS.EPSON,
    pid: 3617,
  },
  [TM_T20]: { name: TM_T20, kind: PRINTER_KINDS.RECEIPT, brand: PRINTER_BRANDS.EPSON, pid: 3617 },
  [TM_T20II]: {
    name: TM_T20II,
    kind: PRINTER_KINDS.RECEIPT,
    brand: PRINTER_BRANDS.EPSON,
    pid: 3605,
  },
  [TM_T20III]: {
    name: TM_T20III,
    kind: PRINTER_KINDS.RECEIPT,
    brand: PRINTER_BRANDS.EPSON,
    pid: 3624,
  },
  [TM_T82]: { name: TM_T82, kind: PRINTER_KINDS.RECEIPT, brand: PRINTER_BRANDS.EPSON, pid: 3601 },
  [TM_M10]: { name: TM_M10, kind: PRINTER_KINDS.RECEIPT, brand: PRINTER_BRANDS.EPSON, pid: 3615 },
  [TM_M30]: {
    name: TM_M30,
    kind: PRINTER_KINDS.RECEIPT,
    brand: PRINTER_BRANDS.EPSON,
    pid: 3616,
    vid: 1208,
  },
  [TM_M30II]: {
    name: TM_M30II,
    kind: PRINTER_KINDS.RECEIPT,
    brand: PRINTER_BRANDS.EPSON,
    pid: 3626,
    vid: 1208,
  },
  [TSP650]: {
    name: TSP650,
    kind: PRINTER_KINDS.RECEIPT,
    brand: PRINTER_BRANDS.STAR,
    pid: 1,
    vid: 0x0519,
  },
  [TSP100]: {
    name: TSP100,
    kind: PRINTER_KINDS.RECEIPT,
    brand: PRINTER_BRANDS.STAR,
    pid: 3,
    vid: 0x0519,
  },
  [MC_PRINT3]: {
    name: MC_PRINT3,
    kind: PRINTER_KINDS.RECEIPT,
    brand: PRINTER_BRANDS.STAR,
    pid: 71,
    vid: 0x0519,
  },
  [LP2824]: {
    name: LP2824,
    kind: PRINTER_KINDS.LABEL,
    brand: PRINTER_BRANDS.ZEBRA,
    pid: 1, // TODO insert correct values here
    vid: 1, // TODO insert correct values here
  },
  [LR2000]: {
    name: LR2000,
    kind: PRINTER_KINDS.RECEIPT,
    brand: PRINTER_BRANDS.LOGIC_CONTROLS,
    pid: 33054,
    vid: 4070,
  },
};

export const isSupportedPrinter = (str: string): str is SupportedPrinterModel =>
  !!(PRINTER_INFORMATION as Record<string, PrinterInformation>)[str];

const pidToPrinterMap = mapKeys(PRINTER_INFORMATION, ({ pid }) => pid);

export const PID_TO_PRINTER_MAP = pidToPrinterMap;
